import { useCallback, useRef } from "react";

import { useJitsu } from "@jitsu/react";
import type { EventPayload } from "@jitsu/sdk-js";

import { JitsuEvent } from "@/data/events/JitsuEvent";

type TrackOnce = (
  event: JitsuEvent,
  properties?: EventPayload,
  options?: {
    /**
     * The thing the event is being sent about — an error object, an id. The
     * event is sent again when this is replaced.
     */
    onceFor?: unknown;
  }
) => void;

/**
 * Sends an event the first time it is asked for, and ignores every later ask.
 *
 * Only the last `onceFor` value per event is remembered. If two callers share
 * the same event but use different `onceFor` values - they'll keep resetting
 * each other.
 */
export function useTrackOnce(): TrackOnce {
  const { track } = useJitsu();
  const sentFor = useRef<Map<JitsuEvent, unknown> | null>(null);

  return useCallback<TrackOnce>(
    (event, properties, options) => {
      const sent = (sentFor.current ??= new Map());
      // Falls back to the event, which is never nullish, so a miss and a
      // recorded nullish key cannot be confused.
      const watching = options?.onceFor ?? event;
      if (sent.get(event) === watching) return;
      // Recorded before the send so a re-entrant render cannot send it twice.
      sent.set(event, watching);
      track(event, properties);
    },
    [track]
  );
}
